
/**
 * Badge ticker (template snippet) integrated into JS module.
 */
export function initBadgeTicker({
    listId = 'badge-ticker-list',
    countdownId = 'badge-refresh',
    tabHrefId = 'tab-href',
    toggleSelector = '#simulator-play-btn',
    arrivingVisitorsAtOnce = 1,
    refreshMs = 10_000,
} = {}) {
    // Set visible link to current /checkin/<id> page
    const tabRef = document.getElementById(tabHrefId);
    const target = `https://${location.host}${location.pathname}`;
    if (tabRef) {
        tabRef.href = target;
        tabRef.innerHTML = target;
    }

    const list = document.getElementById(listId);
    const refreshEl = document.getElementById(countdownId);
    if (!list || !refreshEl) return;

    const toggleBtn = document.querySelector(toggleSelector) || null;

    let nextAt = 0;        // timestamp of next refresh
    let shownCount = 0;    // stream-wide counter: every 3rd badge is fake
    let paused = true;    // pause state
    let tickInterval = null;
    let countdownInterval = null;

    function uuidv4() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
            try {
                return window.crypto.randomUUID();
            } catch (e) { }
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0,
                v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    function getRealBadgeUuid() {
        const arr = window.realBadgeUuids;
        if (!Array.isArray(arr) || arr.length === 0) return false;
        const idx = Math.floor(Math.random() * arr.length);
        const val = arr[idx];
        return (val === undefined || val === null) ? false : val;
    }

    function makeItem(uuid, isFake) {
        const el = document.createElement('code');
        el.className = isFake ? 'text-danger' : 'text-success';
        el.style.color = isFake ? '#dc3545' : '#198754'; // fallback if no CSS framework
        el.title = isFake ? 'Fake badge (generated)' : 'Real badge';
        el.textContent = uuid;
        return el;
    }

    function nextBadge() {
        shownCount++;
        let isFake = (shownCount % 3 === 0); // every 3rd is fake
        let uuid = null;

        if (!isFake) {
            uuid = getRealBadgeUuid();
            if (!uuid) {
                console.error('No realBadgeUuids available');
                setPaused(true);
            }
        }

        if (!uuid) {
            isFake = true;
            uuid = uuidv4();
        }

        return { uuid, isFake };
    }

    function tick() {

        if (paused) return; // do not render new batch while paused

        list.innerHTML = '';
        const batch = [];

        for (let i = 0; i < arrivingVisitorsAtOnce; i++) {
            const { uuid, isFake } = nextBadge();
            list.appendChild(makeItem(uuid, isFake));
            batch.push({ uuid, isFake });
        }
        // set next deadline for countdown
        nextAt = Date.now() + refreshMs;

        // notify listeners & trigger real scan(s)
        try { window.simulatorDispatchBatch?.(batch); } catch { }

    }

    function updateCountdown() {
        if (!refreshEl) return;
        if (paused) {
            refreshEl.textContent = 'Paused';
            list.innerHTML = '-';
            return;
        }
        const remainingMs = Math.max(0, nextAt - Date.now());
        const remainingSec = Math.ceil(remainingMs / 1000);
        refreshEl.textContent = `Refresh in ${remainingSec}s`;
    }

    function startTimers() {
        clearTimers();
        tickInterval = setInterval(tick, refreshMs);
        countdownInterval = setInterval(updateCountdown, 1000);
    }

    function clearTimers() {
        if (tickInterval) {
            clearInterval(tickInterval);
            tickInterval = null;
        }
        if (countdownInterval) {
            clearInterval(countdownInterval);
            countdownInterval = null;
        }
    }

    function setPaused(next) {
        paused = !!next;
        if (paused) {
            clearTimers();
            updateCountdown(); // show 'Paused'
            try { window.dispatchEvent(new CustomEvent('badgeTicker:pause')); } catch { }
            toggleBtn?.classList.remove('playing');
            toggleBtn?.setAttribute('aria-label', 'Play');
        } else {
            // On resume, render a fresh batch immediately, then schedule.
            tick();
            startTimers();
            updateCountdown();
            try { window.dispatchEvent(new CustomEvent('badgeTicker:resume')); } catch { }
            toggleBtn?.classList.add('playing');
            toggleBtn?.setAttribute('aria-label', 'Pause');
        }
    }

    function toggle() {
        setPaused(!paused);
    }

    // Start paused by default
    setPaused(true);

    // Wire up optional play/pause button
    if (toggleBtn) {
        // reflect paused state
        toggleBtn.classList.remove('playing');
        toggleBtn.setAttribute('aria-label', 'Play');
        toggleBtn.addEventListener('click', (e) => {
            e.preventDefault();
            toggle();
        });
    }

    // Expose a tiny API for external control (optional)
    const api = {
        pause: () => setPaused(true),
        resume: () => setPaused(false),
        toggle,
        get paused() { return paused; }
    };
    // Provide a global escape hatch for debugging if needed
    window.badgeTicker = api;

    return api;
}
